import { CheckPermissionsModes, checkPermissions, useAuth } from "@elit-hair/elit-tools";
import { SidebarMenu } from "@elit-hair/elit-ui";

import { appRoutes } from "@/pages/app-routes";
import type { NavigationRoute } from "@/types/navigation-route";

import { SidebarMenuItem } from "./sidebar-menu-item";

export type NavItem = {
    id?: string;
    to?: string;
    title?: string;
    icon?: React.ElementType | null;
    permissions?: string[];
    permissionMode?: CheckPermissionsModes;
    active?: boolean;
    order?: number;
    children?: NavItem[];
};

function mapRouteToNavItem(route: NavigationRoute): NavItem {
    const children = route.children
        ?.filter((child) => child.meta.visibleInSidebar)
        .sort((a, b) => a.meta.order - b.meta.order)
        .map(mapRouteToNavItem);

    return {
        id: route.to ?? route.path ?? route.meta.label,
        to: route.to ?? route.path,
        title: route.meta.label,
        icon: route.meta.icon,
        permissions: route.meta.permissions,
        permissionMode: route.meta.permissionMode,
        order: route.meta.order,
        children,
    };
}

function sortNavItems(items: NavItem[]): NavItem[] {
    return items
        .slice()
        .sort((a, b) => {
            const aHas = typeof a.order === "number";
            const bHas = typeof b.order === "number";
            if (aHas && bHas) return (a.order as number) - (b.order as number);
            if (aHas) return -1;
            if (bHas) return 1;
            return 0;
        })
        .map((it) => {
            if (it.children && it.children.length > 0) {
                return { ...it, children: sortNavItems(it.children) };
            }
            return it;
        });
}

export function collectNavTree(): NavItem[] {
    const visibleRoutes = appRoutes
        .filter((route) => route.meta.visibleInSidebar)
        .sort((a, b) => a.meta.order - b.meta.order);

    const items = visibleRoutes.map(mapRouteToNavItem);
    return sortNavItems(items);
}

export function NavigationItems() {
    const { userData } = useAuth();
    const items = collectNavTree();

    return (
        <SidebarMenu>
            {items.map((item) => {
                const routePermissionCheck = checkPermissions(
                    userData?.permissionCodes,
                    item.permissions,
                    item.permissionMode
                );

                if (!routePermissionCheck) {
                    return null;
                }

                if (item.children && item.children.length > 0) {
                    const filteredChildren = item.children.filter((child) =>
                        checkPermissions(userData?.permissionCodes, child.permissions, child.permissionMode)
                    );

                    if (filteredChildren.length === 0 && !item.title && !item.icon) {
                        return null;
                    }

                    item = {
                        ...item,
                        children: filteredChildren,
                    };
                }

                return <SidebarMenuItem key={item.id} {...item} />;
            })}
        </SidebarMenu>
    );
}
